import { useRef } from 'react'

import {
  Card,
  ConfirmationModal,
  IconButton,
  PrimaryButton,
  Space,
  TertiaryButton,
  WithTooltip,
} from '@procurify/ui'
import { type FormikProps } from 'formik'

import { TabContentFooter } from '@webapp/components/TabContentFooter'
import { TabPanes } from '@webapp/components/TabPanes'
import { PageActions } from '@webapp/core/components/Page'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/ExpenseReportForm'
import { EditExpenseLineItemDrawer } from '@webapp/modules/expense/feature-sets/EditExpenseLineItemDrawer'
import { ExpenseCommentPopoverButton } from '@webapp/modules/expense/feature-sets/ExpenseView/components'
import { EditExpenseReportPresenter } from '@webapp/modules/expense/features/edit-expense-report/presenters/EditExpenseReportPresenter'
import { ExpenseApprovalHistoryPresenter } from '@webapp/modules/expense/features/get-expense-approval-history/presenters/ExpenseApprovalHistoryPresenter'
import { ExpenseCostSummaryPresenter } from '@webapp/modules/expense/features/get-expense-report-detail/presenters/ExpenseCostSummaryPresenter'
import { ApprovalReminderIconButton } from '@webapp/modules/expense/features/send-approval-reminder/presenters/ApprovalReminderIconButton'
import messages from './messages'
import { ExpenseEditViewStyled } from './styles/ExpenseEditViewStyled'
import { useExpenseReportEditView } from './useExpenseReportEditView'

export interface IExpenseReportEditViewProps {
  uuid: string
}

export const ExpenseReportEditView = ({
  uuid,
}: IExpenseReportEditViewProps) => {
  const intl = useProcurifyIntl()
  const formRef = useRef<FormikProps<ExpenseReportFormSchema> | null>(null)

  const {
    data: { expense, editingItem, drawerOpen, isSubmitting },
    states: {
      isDuplicateConfirmOpen,
      isRequester,
      showComments,
      expenseContentTypeId,
      userId,
    },
    methods: {
      onLineItemEditClick,
      onLineItemEditSuccess,
      onLineItemEditCancel,
      onEditSuccess,
      onEditCancel,
      onSubmittingChange,
    },
    commands: { duplicateExpense, confirmDuplicate, closeDuplicateConfirm },
  } = useExpenseReportEditView({ uuid })

  const hasLogs = !!expense?.hasLogs
  const nextApprover = expense?.nextApprover ?? null
  const showSendReminder =
    isRequester && !!expense?.isPending && nextApprover !== null

  return (
    <>
      <PageActions>
        <Space direction='horizontal'>
          {isRequester && (
            <WithTooltip
              title={intl.formatMessage(messages.duplicateToolbarButton)}
            >
              <IconButton
                icon='duplicate'
                aria-label={intl.formatMessage(messages.duplicateToolbarButton)}
                onClick={duplicateExpense}
              />
            </WithTooltip>
          )}

          {hasLogs && expense && (
            <ExpenseApprovalHistoryPresenter logs={expense.logs} />
          )}

          {showComments && expense && (
            <ExpenseCommentPopoverButton
              contentTypeId={expenseContentTypeId}
              objectId={expense.id}
              userId={userId}
            />
          )}

          {showSendReminder && expense && nextApprover && (
            <ApprovalReminderIconButton
              expenseId={expense.id}
              recipient={nextApprover}
            />
          )}
        </Space>
      </PageActions>

      <ExpenseEditViewStyled>
        <Card>
          <Card.Content>
            <Space size='large'>
              <TabPanes activeKey='edit'>
                <TabPanes.Pane
                  label={intl.formatMessage(messages.editTabLabel)}
                  key='edit'
                >
                  {expense && (
                    <EditExpenseReportPresenter
                      expense={expense}
                      formRef={formRef}
                      onSuccess={onEditSuccess}
                      onEditItem={onLineItemEditClick}
                      onSubmittingChange={onSubmittingChange}
                    />
                  )}

                  {expense && (
                    <TabContentFooter isSticky>
                      <Space direction='vertical'>
                        <Space direction='horizontal' justify='end'>
                          <ExpenseCostSummaryPresenter uuid={uuid} />
                        </Space>
                        <Space
                          direction='horizontal'
                          justify='end'
                          align='center'
                          size='large'
                        >
                          <TertiaryButton
                            type='button'
                            onClick={onEditCancel}
                            disabled={isSubmitting}
                          >
                            {intl.formatMessage(messages.cancelButton)}
                          </TertiaryButton>
                          <PrimaryButton
                            onClick={() => formRef.current?.submitForm()}
                            disabled={isSubmitting}
                          >
                            {intl.formatMessage(messages.saveButton)}
                          </PrimaryButton>
                        </Space>
                      </Space>
                    </TabContentFooter>
                  )}
                </TabPanes.Pane>
              </TabPanes>
            </Space>
          </Card.Content>
        </Card>
      </ExpenseEditViewStyled>

      {editingItem && expense && (
        <EditExpenseLineItemDrawer
          drawerOpen={drawerOpen}
          onClose={onLineItemEditCancel}
          item={editingItem}
          expenseReportId={expense.id}
          requesterId={expense.requester.id}
          secondaryCategoryId={expense.secondaryCategory.id}
          onSuccess={onLineItemEditSuccess}
        />
      )}

      <ConfirmationModal
        open={isDuplicateConfirmOpen}
        type='danger'
        header={intl.formatMessage(messages.duplicateOverrideConfirmHeader)}
        message={intl.formatMessage(messages.duplicateOverrideConfirmMessage)}
        onConfirm={async (done) => {
          await confirmDuplicate()
          done()
        }}
        handleDismiss={closeDuplicateConfirm}
      />
    </>
  )
}

ExpenseReportEditView.displayName = 'ExpenseReportEditView'
